import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { FaPlus } from 'react-icons/fa6';
import { MdDelete } from 'react-icons/md';
import { IoChevronDownOutline } from 'react-icons/io5';
import { useFieldArray, useWatch } from 'react-hook-form';
import Button from '@/components/Common/Button';
import Select from '@/components/Common/Select';
import { useGetCurveDentalOperatories } from '@/apis/business/setting/integrations/hook';
import FormSchemaParser from '@/components/Common/FormSchemaParser';

interface CurveDentalFormProps {
  namePrefix: string;
  control: any;
  disabled?: boolean;
  integrationId: string;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

export default function CurveDentalForm({
  namePrefix,
  control,
  disabled,
  integrationId,
  configFields,
  spacing,
  submitForm
}: CurveDentalFormProps) {
  const [expanded, setExpanded] = useState(false);
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${namePrefix}.configuration`
  });

  const formFields = configFields?.filter((field: any) => field.key !== 'providers');
  const providersField = configFields?.find((field: any) => field.key === 'providers');
  const values = useWatch({ control, name: `${namePrefix}.configuration` }) || [];

  useEffect(() => {
    if (values?.length > 0) setExpanded(true);
  }, [values.length]);

  return (
    <>
      <FormSchemaParser
        disabled={disabled}
        fields={formFields || []}
        control={control}
        namePrefix='configuration'
        gridSize={12}
        spacing={spacing}
        submitForm={(val: any) => submitForm(val)}
      />

      {providersField?.group?.[0]?.options?.length > 0 && (
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded((prev) => !prev)}
          disabled={disabled}
          sx={{
            boxShadow: 'none',
            border: '1px solid #F3F3F3',
            borderRadius: 2,
            mt: 2,
            '&::before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
            aria-controls='curve-dental-config-content'
            id='curve-dental-config-header'
          >
            <Typography variant='h6' fontSize={16} fontWeight={600}>
              {providersField?.label}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            {fields.map((field, index) => (
              <ProviderOperatoryRow
                key={field.id}
                providerOptions={providersField?.group?.[0]?.options}
                appointmentTypeOptions={providersField?.group?.[1]?.options}
                index={index}
                namePrefix={namePrefix}
                control={control}
                remove={remove}
                disabled={disabled}
                integrationId={integrationId}
              />
            ))}

            {!disabled && (
              <Button
                type='button'
                color='secondary'
                size='small'
                variant='outlined'
                startIcon={<FaPlus />}
                onClick={() => append({ provider: '', operatory: '', appointment_type: [] })}
                sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
              >
                Add Configuration
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}

interface ProviderOperatoryRowProps {
  index: number;
  namePrefix: string;
  control: any;
  remove: (index: number) => void;
  disabled?: boolean;
  integrationId?: string;
  providerOptions: any[];
  appointmentTypeOptions: any[];
}

const EMPTY_CONFIGURATION: any[] = [];

const ProviderOperatoryRow = ({
  index,
  namePrefix,
  control,
  remove,
  disabled,
  integrationId,
  providerOptions,
  appointmentTypeOptions
}: ProviderOperatoryRowProps) => {
  // Watch the entire configuration array to manage unique selections
  const watchedConfiguration = useWatch({
    control,
    name: `${namePrefix}.configuration`
  });

  const configurationValues = useMemo(
    () => (Array.isArray(watchedConfiguration) ? watchedConfiguration : EMPTY_CONFIGURATION),
    [watchedConfiguration]
  );

  const provider = configurationValues[index]?.provider;
  const currentOperatory = configurationValues[index]?.operatory;

  const { data: OperatoryOptions, isFetching } = useGetCurveDentalOperatories(integrationId, provider);

  const filteredOperatoryOptions = useMemo(() => {
    const all = OperatoryOptions?.operatories ?? [];
    if (!provider) return all;

    const usedElsewhere = configurationValues
      .map((conf: any, i: number) => {
        if (i === index) return null;
        if (String(conf?.provider ?? '') !== String(provider)) return null;
        return conf?.operatory;
      })
      .filter((v: any) => v !== null && v !== undefined && v !== '');

    return all.filter(
      (opt: any) =>
        !usedElsewhere.some((used: any) => String(used) === String(opt.value)) ||
        String(opt.value) === String(currentOperatory)
    );
  }, [OperatoryOptions?.operatories, provider, configurationValues, index, currentOperatory]);

  return (
    <Box display='flex' alignItems='center' gap={2} mb={2}>
      <Select
        name={`${namePrefix}.configuration[${index}].provider`}
        control={control}
        label='Provider'
        disabled={disabled}
        required
        options={providerOptions ?? []}
      />
      <Select
        name={`${namePrefix}.configuration[${index}].operatory`}
        control={control}
        label='Operatory'
        disabled={disabled || !provider || isFetching}
        required
        options={filteredOperatoryOptions}
      />
      <Select
        name={`${namePrefix}.configuration[${index}].appointment_type`}
        control={control}
        label='Appointment Type'
        required
        disabled={disabled}
        multiple
        options={appointmentTypeOptions}
      />

      {!disabled && (
        <IconButton size='small' color='error' onClick={() => remove(index)}>
          <MdDelete />
        </IconButton>
      )}
    </Box>
  );
};
